import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  //useMediaQuery,
  Drawer,
  Button,
  CircularProgress,
} from '@mui/material';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  useGetTableDataForTimeRangeV2Query,
  useLazyGetTableDataForTimeRangeV2Query,
} from '../../../../services/mVPGraphQlService';
import {
  AutoCompleteController,
  InputController,
  Table,
} from '../../../../components';
import { TFieldDetails2 } from '../../../../types/mvpCharts';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import * as yup from 'yup';
import { MVPFiltersProps } from '../../constants';
import { useAlertHelper } from '../../../../utils/functions/hooks';
import {
  calculateDateDifference,
  timeStampDiff,
} from '../../../../utils/constants/calendar';
import { useDebounce } from '../../../../services/customHook/debounceHook';
import { useRowSelect } from '../../../../components/Table/actionNodes';
import Chip from '@mui/material/Chip';
import LaunchIcon from '@mui/icons-material/Launch';

// import { SideSheet } from '../../reports/sideSheet';
import { InspectionRequest } from '../../reports/inspectionRequest';
import { useFilterContext } from '../../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import React from 'react';
import { MVPFilters } from '../../filters';
import { GoogleMaps } from '../googleMaps';
import CommonAppBar from '../../../../components/commonAppBar';

const drawerWidth = 360;
const currDate = new Date();
const formSchema = yup.object({
  startDate: yup
    .date()
    .default(dayjs(currDate).subtract(100, 'days').toDate())
    .required('Start Date is required'),
  endDate: yup
    .date()
    .default(dayjs(currDate).toDate())
    .required('End Date is required')
    .test(
      'max-date-range',
      'The date range cannot be more than 100 days',
      function (value) {
        const { startDate } = this.parent;
        return (
          dayjs(value).diff(dayjs(startDate), 'days') <= 100
        );
      }
    ),
  date: yup
    .string()
    .default('TILL_HARVEST_OR_CURR_DATE_TYPE'),

  intValue: yup
    .number()
    .min(0, 'Number must be at least 0')
    .max(200, 'Number cannot exceed 200')
    .typeError('Only numbers are allowed')
    .default(0),
  displayType: yup.string().default('DATA_TABLE'),
});

export const MVPDataTable: React.FC<MVPFiltersProps> = ({
  handleApplyFilter,
  onNavigate,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    register,
  } = useFormWithSchema(formSchema, { mode: 'onTouched' });

  const {
    checkbox,
    selectAllCheckBox,
    seletedIds,
    setSeletedIds,
  } = useRowSelect();
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );

  const [open, setOpen] = useState(false);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<
    TFieldDetails2[]
  >([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [allTableData, setAllTableData] = useState<
    TFieldDetails2[]
  >([]);
  // const isMobileOrTablet = useMediaQuery(
  //   '(max-width:1024px)'
  // );
  const { showError } = useAlertHelper();

  const debouncedIntValue = useDebounce(
    Number(watch('intValue')),
    1000
  );
  const [buttonClick, setButtonClick] = useState({
    minValue: 0,
    maxValue: 100,
  });
  const shouldFetchData =
    typeof debouncedIntValue === 'number' &&
    debouncedIntValue <= 200;

  const { data, isFetching, refetch } =
    useGetTableDataForTimeRangeV2Query(
      {
        commonFilters,
        wsId,
        daysAfterPlanting: debouncedIntValue,
        tabContext: 'DATA_TAB',
        tableDataType: watch('date'),
        tablePageSize: pageSize,
        tablePageNumber: pageNo + 1,
        percentileRange: {
          minPercentile: buttonClick.minValue,
          maxPercentile: buttonClick.maxValue,
        },
      },

      {
        skip:
          !shouldFetchData ||
          watch('displayType') == 'MAPS',
      }
    );

  const [getTableDataIntoCSV] =
    useLazyGetTableDataForTimeRangeV2Query();
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      await getTableDataIntoCSV({
        commonFilters,
        daysAfterPlanting: watch('intValue'),
        tabContext: 'DATA_TAB',
        tableDataType: watch('date'),
        downloadCsv: true,
        tablePageSize: pageSize,
        tablePageNumber: pageNo + 1,
        percentileRange: {
          minPercentile: buttonClick.minValue,
          maxPercentile: buttonClick.maxValue,
        },
        wsId,
      })
        .unwrap()
        .then((response) => {
          const url =
            response?.data?.GetTableDetailsV2
              ?.csvPresignedUrl;
          window.open(url, '_blank');
        })
        .catch(() => {
          showError('Error while downloading file');
        });
    } finally {
      setIsDownloading(false);
    }
  };
  const user = useSelector(
    (state: RootState) => state.authUser
  );
  type Column<T, K extends keyof T> = {
    key: K;
    label: React.ReactNode;
    cellModifier?: (data: T) => React.ReactNode | string;
  };

  // const [selectedField, setSelectedField] =
  //   useState<TFieldDetails2 | null>(null);
  // const [sideSheetOpen, setSideSheetOpen] = useState(false);

  // const openSideSheet = (data: TFieldDetails2) => {
  //   setSelectedField(data);
  //   setSideSheetOpen(true);
  // };

  // const closeSideSheet = () => {
  //   setSideSheetOpen(false);
  // };
  const columns: Column<
    TFieldDetails2,
    keyof TFieldDetails2
  >[] = [
    {
      key: 'selectRow',
      label:
        user?.role === 'FA' ? (
          <Checkbox disabled />
        ) : (
          selectAllCheckBox(
            data?.data?.GetTableDetailsV2?.fieldDetails?.map(
              (ele: TFieldDetails2) => ele.fieldNumber
            ) || []
          )
        ),
      cellModifier: (data: TFieldDetails2) =>
        user?.role === 'FA' ? (
          <Checkbox disabled />
        ) : (
          checkbox(data.fieldNumber)
        ),
    },

    {
      key: 'fieldNumber',
      label: 'Field Number',
      cellModifier: (data: TFieldDetails2) => {
        return actionNode(data);
      },
    },

    {
      key: 'fieldDetails',
      label: 'Region',
      cellModifier(data) {
        return data?.fieldDetails?.geographyData?.region;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Village',
      cellModifier(data) {
        return data?.fieldDetails?.geographyData?.village;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Registered Area',
      cellModifier(data) {
        return data.fieldDetails?.registeredArea?.toFixed(
          2
        );
      },
    },
    {
      key: 'fieldDetails',
      label: 'Geofence Area',
      cellModifier(data) {
        return data.fieldDetails.geofenceArea?.toFixed(2);
      },
    },
    {
      key: 'fieldDetails',
      label: 'Area Under reported',
      cellModifier(data) {
        return data.fieldDetails?.areaUnderReported?.toFixed(
          2
        );
      },
    },
    {
      key: 'fieldDetails',
      label: 'Grower Name',
      cellModifier(data) {
        return data.fieldDetails?.growerName;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Organizer',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData?.organizer;
      },
    },
    {
      key: 'fieldDetails',
      label: 'FA',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData?.fa;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Regional Manager',
      cellModifier(data) {
        return data?.fieldDetails?.employeeData
          ?.regionManager;
      },
    },
    {
      key: 'fieldDetails',
      label: 'Sowing Date',
      cellModifier(data) {
        return data?.fieldDetails?.sowingTime?.seconds > 0
          ? dayjs(
              data?.fieldDetails?.sowingTime?.seconds
                ? data?.fieldDetails?.sowingTime?.seconds *
                    1000
                : 0
            ).format('DD/MM/YYYY')
          : '-';
      },
    },
    {
      key: 'fieldDetails',
      label: 'Harvest Date',
      cellModifier(data) {
        return data?.fieldDetails?.harvestTime?.seconds == 0
          ? '-'
          : dayjs(
              data?.fieldDetails?.harvestTime?.seconds
                ? data?.fieldDetails?.harvestTime?.seconds *
                    1000
                : 0
            ).format('DD/MM/YYYY');
      },
    },
    {
      key: 'fieldDetails',
      label: 'Harvest Quantity',
      cellModifier(data) {
        return data?.fieldDetails?.harvestQuantity;
      },
    },
    {
      key: 'totalDays',
      label: 'Total days',
      cellModifier(data) {
        const sowingDate = dayjs(
          data?.fieldDetails?.sowingTime?.seconds
            ? data?.fieldDetails?.sowingTime?.seconds * 1000
            : 0
        ).format('DD/MM/YYYY');
        const harvestDate =
          data?.fieldDetails?.harvestTime?.seconds == 0
            ? '-'
            : dayjs(
                data?.fieldDetails?.harvestTime?.seconds
                  ? data?.fieldDetails?.harvestTime
                      ?.seconds * 1000
                  : 0
              ).format('DD/MM/YYYY');
        const difference = calculateDateDifference(
          sowingDate,
          harvestDate
        );
        return difference.days || '-';
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Average Index',
      cellModifier(data) {
        return data.averageIndex?.toString();
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Min Index',
      cellModifier(data) {
        return data.minIndex?.toString();
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Max Index',
      cellModifier(data) {
        return data.maxIndex?.toString();
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Standard Deviation',
      cellModifier(data) {
        return data.standardDeviation?.toString();
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Latest DOP',
      cellModifier(data) {
        const difference = timeStampDiff(
          data?.fieldDetails?.sowingTime?.seconds,
          data?.derivedFieldProps?.latestIndexTime?.seconds
        );

        return `${
          difference.differenceDays > 0
            ? difference.differenceDays
            : '-'
        } days`;
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'No. of Data points',
      cellModifier(data) {
        return data.dataPoints?.toString();
      },
    },
    {
      key: 'derivedFieldProps',
      label: 'Percentile',
      cellModifier(data) {
        return (
          <span
            style={{
              color:
                data.percentile < 21
                  ? 'rgba(235, 87, 87, 1)'
                  : '',
            }}>
            {data.percentile?.toString()}%
          </span>
        );
      },
    },
  ];

  const actionNode = (tableRowData: TFieldDetails2) => {
    const data = tableRowData;

    const handleNavigation = () => {
      localStorage.setItem(
        'fieldId',
        data.fieldNumber?.toString()
      );
      localStorage.setItem(
        'filterIndex',
        JSON?.stringify(commonFilters)
      );

      onNavigate?.('timeseries', {
        fieldId: data.fieldNumber?.toString(),
      });
    };

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Typography fontSize={13} fontWeight={600}>
          {data.fieldNumber}
        </Typography>
        <Tooltip title="Navigate to field" arrow>
          <IconButton
            className="close-icon-btn"
            type="button"
            onClick={() => handleNavigation()}>
            <LaunchIcon className="close-icon" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  useEffect(() => {
    if (debouncedIntValue > 200) {
      showError('Number of days cannot exceed 200');
      return;
    }
    if (data?.data?.GetTableDetailsV2?.fieldDetails) {
      setTableData(
        data?.data?.GetTableDetailsV2?.fieldDetails
      );
      setTotalCount(
        data?.data?.GetTableDetailsV2?.totalCount || 0
      );
      setAllTableData(
        data?.data?.GetTableDetailsV2?.fieldDetails
      );
    }
  }, [data, debouncedIntValue]);

  const handlePageChange = (
    event: React.MouseEvent | null,
    newPageNumber: number
  ) => {
    setPageNo(newPageNumber);
    setTableData(
      getPaginatedData(
        newPageNumber,
        pageSize,
        allTableData
      )
    );
  };

  const getPaginatedData = (
    newPageNumber: number,
    itemsPerPage: number,
    allItemsList: TFieldDetails2[]
  ) => {
    const indexOfLastItem =
      (newPageNumber + 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPageItems = allItemsList?.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    return [...currentPageItems];
  };

  const dateFieldOptions = [
    {
      value: 'TILL_HARVEST_OR_CURR_DATE_TYPE',
      label: 'Till Harvest/Current Date',
    },
    {
      value: 'DAY_AFTER_PLANTING_TYPE',
      label: 'Days after Planting',
    },
  ];
  const typeFielDOptions = [
    {
      value: 'DATA_TABLE',
      label: 'List',
    },
    {
      value: 'MAPS',
      label: 'Maps',
    },
  ];
  // const onSubmit = (data) => {};

  const [selectedRowIds, setSelectedRowIds] = useState<
    number[]
  >([]);
  console.log(selectedRowIds);
  const handleSelectionChange = (ids: number[]) => {
    setSelectedRowIds(ids);
  };

  const [filterOpen, setFilterOpen] = useState(false);

  const toggleFilter = () => setFilterOpen((prev) => !prev);
  return (
    <>
      <CommonAppBar
        section={
          <>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              wrap="nowrap"
              sx={{ width: '100%' }}>
              <Grid
                item
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#212121',
                    fontSize: 14,
                  }}>
                  All Fields ({totalCount})
                </Typography>

                <Button
                  onClick={toggleFilter}
                  variant="outlined"
                  startIcon={<FilterAltOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#ccc',
                    color: '#333',
                    fontSize: 14,
                    background: '#fff',
                    '&:hover': {
                      borderColor: '#888',
                      backgroundColor: '#fafafa',
                    },
                  }}>
                  Filter
                </Button>

                <Box
                  sx={{
                    width: 250,
                    marginTop: 9,
                    zIndex: 1002,
                    position: 'relative',
                  }}>
                  <AutoCompleteController
                    label=""
                    name="date"
                    control={control}
                    options={dateFieldOptions}
                    error={errors.date?.message}
                    disableClearable
                    disablePortal={false}
                    height={38}
                  />
                </Box>
                {watch('date') ===
                  'DAY_AFTER_PLANTING_TYPE' && (
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={2}
                    lg={2}
                    style={{ marginTop: 48 }}>
                    <InputController
                      label=""
                      register={register('intValue')}
                      type="number"
                      required
                      error={errors.intValue?.message}
                      inputHeight={'40px'}
                    />
                  </Grid>
                )}

                {watch('displayType') == 'DATA_TABLE' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[
                      {
                        label: '0%-20%',
                        minValue: 0,
                        maxValue: 20,
                      },
                      {
                        label: '20%-50%',
                        minValue: 20,
                        maxValue: 50,
                      },
                      {
                        label: '50%-70%',
                        minValue: 50,
                        maxValue: 70,
                      },
                      {
                        label: '70%-100%',
                        minValue: 70,
                        maxValue: 100,
                      },
                    ].map(
                      ({ label, minValue, maxValue }) => {
                        const isSelected =
                          buttonClick.minValue ===
                            minValue &&
                          buttonClick.maxValue ===
                            maxValue &&
                          !(
                            buttonClick.minValue === 0 &&
                            buttonClick.maxValue === 100
                          );

                        return (
                          <Chip
                            key={label}
                            label={label}
                            onClick={() =>
                              setButtonClick((prev) =>
                                prev.minValue ===
                                  minValue &&
                                prev.maxValue === maxValue
                                  ? {
                                      minValue: 0,
                                      maxValue: 100,
                                    }
                                  : { minValue, maxValue }
                              )
                            }
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              borderRadius: '20px',
                              backgroundColor: isSelected
                                ? 'rgba(0, 105, 112, 0.14)'
                                : '#fff',
                              border:
                                '1px solid rgba(0, 105, 112, 1)',
                              color: 'rgba(0, 105, 112, 1)',
                              '&:hover': {
                                backgroundColor:
                                  'rgba(0, 105, 112, 0.14)',
                              },
                            }}
                          />
                        );
                      }
                    )}
                  </Box>
                )}
              </Grid>

              <Grid
                item
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexShrink: 0,
                }}>
                <Box sx={{ width: 100, marginTop: 9 }}>
                  <AutoCompleteController
                    label=""
                    name="displayType"
                    control={control}
                    options={typeFielDOptions}
                    error={errors.displayType?.message}
                    disableClearable
                    height={38}
                  />
                </Box>

                <Grid item>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <div
                      style={{
                        width: 16,
                        height: 2,
                        backgroundColor:
                          'rgba(253, 175, 32, 1)',
                        marginRight: 8,
                      }}
                    />
                    <span style={{ fontWeight: 500 }}>
                      PLD Fields
                    </span>
                  </div>
                </Grid>

                {user?.role !== 'FA' && (
                  <Grid item>
                    <Button
                      variant="contained"
                      onClick={() => setOpen(true)}
                      disabled={seletedIds?.length === 0}
                      sx={{
                        textTransform: 'none',
                        backgroundColor:
                          'rgba(0, 105, 112, 1)',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        color: '#fff',
                        width: 200,
                        height: 44,
                        opacity:
                          seletedIds?.length > 0 ? 1 : 0.3,
                        '&:hover': {
                          backgroundColor:
                            'rgba(0, 105, 112, 1)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor:
                            'rgba(0, 105, 112, 1)', // same green
                          color: '#fff', // keep text white
                        },
                      }}>
                      Request Inspection
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        }
      />
      <div>
        {watch('displayType') == 'DATA_TABLE' ? (
          <div
            style={{
              padding: 20,
              marginTop: 70,
              //   width: '70%',
            }}>
            <h1 className="text-lg py-5 title-text-container">
              Table ({commonFilters?.index})
              <Button
                variant="text"
                onClick={() => handleDownloadCsv()}
                disabled={
                  tableData?.length > 0 ? false : true
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  color: 'rgba(2, 105, 93, 1)',
                  fontSize: 14,
                }}>
                Download
              </Button>
              {isDownloading && (
                <CircularProgress
                  size={16}
                  color="inherit"
                  style={{ marginLeft: 5 }}
                />
              )}
            </h1>
            <Table
              columns={columns}
              data={tableData?.length ? tableData : []}
              rowsPerPage={pageSize}
              rowKey={'fieldNumber'}
              setRowsPerPage={setPageSize}
              count={totalCount}
              page={pageNo}
              setPage={setPageNo}
              isLoading={isFetching}
              rowsPerPageOptions={[pageSize]}
              pageChangeHandler={handlePageChange}
              selectedIds={seletedIds}
              onSelectionChange={handleSelectionChange}
            />
          </div>
        ) : (
          <div
            style={{
              marginTop: 50,
            }}>
            <GoogleMaps
              onNavigate={onNavigate}
              tableDataType={watch('date')}
              shouldFetchData={shouldFetchData}
              debouncedIntValue={
                watch('date') ==
                'TILL_HARVEST_OR_CURR_DATE_TYPE'
                  ? -1
                  : debouncedIntValue
              }
            />{' '}
          </div>
        )}
      </div>
      <InspectionRequest
        open={open}
        setOpen={setOpen}
        seletedIds={seletedIds}
        setSeletedIds={setSeletedIds}
        fromDataTable={true}
        refetch={refetch}
      />
      {/* <SideSheet
        fieldId={selectedField?.fieldNumber || 0}
        open={sideSheetOpen}
        onClose={closeSideSheet}
      /> */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={filterOpen}
        onClose={toggleFilter}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            p: 2,
            top: 118, // match AppBar height
            left: 60, // open from sidebar edge
            height: 'calc(100% - 64px)', // fill remaining height below header
            borderTop: '1px solid #eee', // subtle divider (optional)
          },
        }}>
        <MVPFilters
          filterValues={commonFilters}
          //   filtersIsCollapsed={filtersIsCollapsed}
          handleApplyFilter={handleApplyFilter}
          setFilterOpen={setFilterOpen}
        />
      </Drawer>
    </>
  );
};
